import { motion, PanInfo } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import SafeImage from './SafeImage';
import { ChevronLeftIcon, ChevronRightIcon } from '@c/icons';
import { ProductDocument } from '@util/types/firestore/products';
import { DraftOverlay, SoldOverlay } from '@c/dashboard/pages/ListingsPage';

interface ProductCarouselProps {
  product: ProductDocument;
  openProductViewer: (index: number) => void;
}

// function that wraps around the index to keep it within the range of 0 to count
const wrap = (index: number, count: number) => {
  if (index < 0) return count - 1;
  if (index >= count) return 0;
  return index;
};

const Dots = ({ count, active }: { count: number; active: number }) => (
  <div className="absolute bottom-0 left-0 right-0 mb-8 flex justify-center gap-[1rem]">
    {new Array(count).fill(1).map((_el, i) => (
      <motion.div
        key={`slider-${i}`}
        className="h-[.8rem] w-[.8rem] rounded-full bg-brand-white"
        initial={false}
        animate={{
          scale: active === i ? 1.5 : 1,
          opacity: active === i ? 1 : 0.5,
        }}
      />
    ))}
  </div>
);

const ImageSlider = ({ images }: Pick<ProductDocument, 'images'>) => {
  const constraintsRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const width =
    (constraintsRef.current && constraintsRef.current.offsetWidth) || 350;

  const dragEndHandler = (
    event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    if (event.type === 'pointercancel') return;
    const offset = info.offset.x;
    if (Math.abs(offset) > 60) {
      const direction = offset < 0 ? 1 : -1;
      setActive(wrap(active + direction, images.length));
    }
  };

  return (
    <div
      className="relative aspect-square w-full overflow-hidden bg-brand-darker-white"
      ref={constraintsRef}
    >
      <motion.div
        dragConstraints={constraintsRef}
        onDragEnd={dragEndHandler}
        drag="x"
        dragElastic={1}
        className="height-[37.5rem] absolute inset-0 flex "
        animate={{
          x: -1 * active * width,
          transition: { duration: 0.25 },
          transitionTimingFunction: 'ease-out',
        }}
      >
        {images.map((image, i) => {
          return (
            <div
              key={`image-${i}-${image.full}`}
              className="relative h-full w-full flex-shrink-0 snap-start bg-brand-primary-lighter"
            >
              <SafeImage
                alt="product"
                src={image.full}
                className="m-auto object-cover"
                draggable={false}
                fill
                height={144}
                width={144}
                disableOptimization
              />
            </div>
          );
        })}
      </motion.div>

      <Dots count={images.length} active={active} />
    </div>
  );
};

const ProductCarousel = ({
  product,
  openProductViewer,
}: ProductCarouselProps) => {
  const [imageIndex, setImageIndex] = useState(0);

  // Hydration-safe: the auction overlays below depend on the wall clock, which
  // differs between the SSR render and the browser. Gate each check on
  // `mounted` so the server and first client render agree (no auction overlay
  // pre-mount), then the real overlay is chosen after mount. We must NOT
  // compare a real timestamp against an epoch-0 sentinel: 0 would make every
  // live/ended auction render the "not started" overlay in cached/no-JS HTML.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <>
      {/* mweb */}
      <div
        className="relative flex w-full sm:hidden"
        onClick={() => openProductViewer(imageIndex)}
      >
        <ImageSlider images={product.images} />
        {product.out_of_stock &&
          !product.is_auction &&
          !product.on_vacation && <SoldOverlay variant="sold" />}
      </div>

      {/* dweb */}
      <div className="hidden w-full max-h-[57rem] overflow-hidden sm:flex">
        <div className="min-w-fit flex-col gap-[1.2rem] overflow-y-scroll hidden lg:flex">
          {product.images.map((image, i) => {
            const isSelected = i === imageIndex;
            return (
              <div
                key={`thumbnail-${i}`}
                className={`relative min-h-[8rem] min-w-[8rem] cursor-pointer transition-opacity ${
                  isSelected ? 'opacity-100' : 'opacity-50'
                }`}
                onClick={() => setImageIndex(i)}
              >
                <SafeImage
                  src={image.thumb}
                  fill
                  alt={`thumbnail-${i} for ${product.title}`}
                  draggable={false}
                  className="object-cover"
                />
              </div>
            );
          })}
        </div>
        <div
          onClick={() => openProductViewer(imageIndex)}
          className="relative ml-[0.5rem] aspect-square w-full cursor-zoom-in"
        >
          <SafeImage
            src={product.images[imageIndex]?.full}
            alt={product.title}
            fill
            className="object-cover"
            key={`image-${imageIndex}`}
            loading="eager"
            disableOptimization
          />
          {product.images.length > 1 && (
            <div className="absolute z-20 mt-auto flex h-[50rem] md:h-[37rem] lg:h-[57rem] w-full items-center justify-between px-[0.5rem]">
              <button
                aria-label="previous"
                onClick={(e) => {
                  e.stopPropagation();
                  setImageIndex(wrap(imageIndex - 1, product.images.length));
                }}
                className="flex h-[4rem] w-[4rem] items-center justify-center rounded-full bg-brand-secondary text-brand-white"
              >
                <ChevronLeftIcon />
              </button>
              <button
                aria-label="next"
                onClick={(e) => {
                  e.stopPropagation();
                  setImageIndex(wrap(imageIndex + 1, product.images.length));
                }}
                className="flex h-[4rem] w-[4rem] items-center justify-center rounded-full bg-brand-secondary text-brand-white"
              >
                <ChevronRightIcon />
              </button>
            </div>
          )}
          {product.is_draft ? (
            <DraftOverlay variant="draft" />
          ) : product.start_time && mounted && product.start_time > Date.now() ? (
            <DraftOverlay variant="auction" />
          ) : product.end_time && mounted && product.end_time < Date.now() ? (
            <SoldOverlay variant="auction" />
          ) : product.out_of_stock &&
            !product.is_auction &&
            !product.on_vacation ? (
            <SoldOverlay variant="sold" />
          ) : null}
        </div>
      </div>
    </>
  );
};

export default ProductCarousel;
